import type { ColumnDef, Row } from "@tanstack/react-table";

import {
  Badge,
  Button,
  Dialog,
  DialogContent,
  FormInput,
  IconCross,
  IconLock,
  IconRegistration,
  IconTick,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  Table,
  TableActionsDropdown,
  TableExpandChevron,
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@sentricmusic/ui";
import moment from "moment";
import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";

import type { Writer } from "@/api/queries";

import { useGetSocieties } from "@/api/queries";
import useGetColours from "@/helpers/getColours";
import { useUIStateStore } from "@/store/uiStateStore";

import { CreateWriterModal } from "../songs/components/CreateWriterModal";
import WriterDetails from "./WriterDetails";

type Props = {
  data: Writer[];
  highlightColour: string;
  adminRights: boolean;
  isLoading?: boolean;
  onWriterUpdated?: () => void;
};

type ColourContext = {
  isTunecore: boolean;
  statusWarningColour: string;
  statusSuccessColour: string;
  statusErrorColour: string;
};

function ExternalWriterColourSelection(
  status: string,
  colours: ColourContext,
): string | null {
  const {
    isTunecore,
    statusWarningColour,
    statusSuccessColour,
    statusErrorColour,
  } = colours;
  switch (status) {
    case "2":
      return isTunecore ? "#EE9E26" : statusWarningColour;
    case "1":
      return isTunecore ? "#00ef86" : statusSuccessColour;
    case "3":
      return isTunecore ? "#F44336" : statusErrorColour;
    default:
      return null;
  }
}

function WritersTable({ data, highlightColour, adminRights, isLoading = false, onWriterUpdated }: Props) {
  const { t } = useTranslation();
  const societies = useGetSocieties();
  const { statusSuccessColour, statusWarningColour, statusErrorColour }
    = useGetColours();
  const isTunecore = useUIStateStore((state) => {
    return state.isTunecore;
  });
  const [nameFilter, setNameFilter] = useState("");
  const [proFilter, setProFilter] = useState("");
  const [caeFilter, setCaeFilter] = useState("");
  const [rtcFilter, setRtcFilter] = useState<"all" | "rtc" | "no-rtc">("all");
  const [editingWriterCode, setEditingWriterCode] = useState<string | null>(null);

  const handleWriterUpdated = (): void => {
    onWriterUpdated?.();
    setEditingWriterCode(null);
  };

  const columns: ColumnDef<Writer>[] = useMemo(() => {
    const colours: ColourContext = {
      isTunecore,
      statusWarningColour,
      statusSuccessColour,
      statusErrorColour,
    };

    const base: ColumnDef<Writer>[] = [
      {
        id: "colWriterTableName",
        header: String(t("writers:writer-name-col")),
        accessorFn: (row) => {
          return [row.firstname, row.middlename, row.lastname]
            .filter(Boolean)
            .join(" ");
        },
        size: 200,
        cell: ({ getValue, row }) => (
          <span className="flex items-center gap-2.5">
            <TableExpandChevron isExpanded={row.getIsExpanded()} onToggle={row.getToggleExpandedHandler()} />
            <span className="flex items-center gap-1.5">
              {getValue<string>()}
              {row.original.islocked === true && (
                <IconLock className="h-3.5 w-3.5 shrink-0 stroke-ink-accent" />
              )}
            </span>
          </span>
        ),
      },
      {
        id: "colWriterTablePRO",
        header: String(t("writers:pro-membership-col")),
        accessorFn: (row) => {
          if (row.societycisaccode == null || !societies.data) {
            return null;
          }
          return (
            societies.data.societies.find((s) => {
              return s.cisaccode === row.societycisaccode;
            })?.name ?? null
          );
        },
        size: 200,
      },
      {
        id: "colWriterTableCAE",
        header: "CAE/IPI#",
        accessorKey: "caeipinumber",
        size: 150,
        cell: ({ getValue }) => {
          const value = getValue<string | undefined>();
          const isStatusCode = value === "1" || value === "2" || value === "3";
          if (!isStatusCode)
            return value != null && value !== "0" ? <span>{value}</span> : null;
          const tooltipText
            = value === "1"
              ? "Registered"
              : value === "2"
                ? "Registration Pending"
                : "Registration Failed";
          return (
            <TooltipProvider>
              <Tooltip>
                <TooltipTrigger asChild>
                  <div className="flex items-center justify-center w-8 h-8 p-2 rounded-lg">
                    <IconRegistration size="lg" stroke={ExternalWriterColourSelection(value, colours) ?? undefined} />
                  </div>
                </TooltipTrigger>
                <TooltipContent>{tooltipText}</TooltipContent>
              </Tooltip>
            </TooltipProvider>
          );
        },
      },
      {
        id: "colWriterTableRTC",
        header: String(t("writers:right-to-collect-col")),
        accessorKey: "writercontrolledworkscount",
        size: 150,
        cell: ({ getValue }) => {
          const count = getValue<number | undefined>() ?? 0;
          return count > 0
            ? <Badge variant="success"><IconTick className="w-3.5 h-3.5 stroke-2" /></Badge>
            : <Badge variant="danger"><IconCross className="w-3.5 h-3.5 stroke-2" /></Badge>;
        },
      },
      {
        id: "colVerifiedState",
        header: String(t("writers:writer-amendment-status")),
        accessorKey: "verifiedstate",
        size: 200,
        cell: ({ row }) => {
          const { verifiedstate, changespending, islocked } = row.original;

          if (verifiedstate === 0) {
            return <Badge variant="warning">{String(t("common:pill-submitted"))}</Badge>;
          }
          if (verifiedstate === 1 && changespending === false) {
            return <Badge variant="success">{String(t("common:pill-processed"))}</Badge>;
          }
          if ((verifiedstate === 1 && changespending === true) || islocked === true) {
            return <Badge variant="warning">{String(t("common:pill-change"))}</Badge>;
          }
          if (verifiedstate === 2) {
            return <Badge variant="danger">{String(t("common:pill-rejected"))}</Badge>;
          }
          return null;
        },
      },
      {
        id: "colWriterTableActions",
        header: "",
        size: 60,
        minSize: 60,
        maxSize: 60,
        enableSorting: false,
        enablePinning: true,
        meta: { pinSide: "right" },
        cell: ({ row }) => (
          <TableActionsDropdown>
            <Button
              variant="dropdown"
              label={String(t("writers:edit-writer"))}
              onClick={(event: React.MouseEvent) => {
                event.stopPropagation();
                if (adminRights) {
                  setEditingWriterCode(row.original.writercode);
                }
              }}
            />
          </TableActionsDropdown>
        ),
      },
    ];

    if (isTunecore) {
      base.splice(1, 0, {
        id: "colWriterTableDOB",
        header: String(t("writers:dob")),
        accessorFn: row =>
          row.dateofbirth != null
            ? moment.utc(row.dateofbirth).format("YYYY-MM-DD")
            : null,
        size: 150,
      });
    }

    return base;
  }, [
    societies.data,
    isTunecore,
    t,
    statusSuccessColour,
    statusWarningColour,
    statusErrorColour,
    adminRights,
  ]);

  const filteredData = useMemo(
    () =>
      data.filter((w) => {
        const fullName = [w.firstname, w.middlename, w.lastname]
          .filter(Boolean)
          .join(" ")
          .toLowerCase();
        const proName = (
          societies.data?.societies.find(
            s => s.cisaccode === w.societycisaccode,
          )?.name ?? ""
        ).toLowerCase();
        if (nameFilter && !fullName.includes(nameFilter.toLowerCase()))
          return false;
        if (proFilter && !proName.includes(proFilter.toLowerCase()))
          return false;
        if (
          caeFilter
          && !(w.caeipinumber ?? "")
            .toLowerCase()
            .includes(caeFilter.toLowerCase())
        ) {
          return false;
        }
        if (rtcFilter === "rtc" && !((w.writercontrolledworkscount ?? 0) > 0))
          return false;
        if (rtcFilter === "no-rtc" && (w.writercontrolledworkscount ?? 0) > 0)
          return false;
        return true;
      }),
    [data, nameFilter, proFilter, caeFilter, rtcFilter, societies.data],
  );

  const filters = useMemo(
    () => (
      <>
        <FormInput
          label={String(t("writers:writer-name-col"))}
          smallFont
          placeholder="Search writer name..."
          value={nameFilter}
          onChange={e => setNameFilter(e.target.value)}
        />
        <FormInput
          label={String(t("writers:pro-membership-col"))}
          smallFont
          placeholder="Search PRO..."
          value={proFilter}
          onChange={e => setProFilter(e.target.value)}
        />
        <FormInput
          label="CAE/IPI#"
          smallFont
          placeholder="Search CAE/IPI..."
          value={caeFilter}
          onChange={e => setCaeFilter(e.target.value)}
        />
        <Select
          value={rtcFilter}
          onValueChange={v => setRtcFilter(v as "all" | "rtc" | "no-rtc")}
        >
          <SelectTrigger
            smallFont
            label={String(t("writers:right-to-collect-col"))}
            className="pl-0 w-full"
          >
            <SelectValue placeholder={String(t("common:all", "All"))} />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">
              {String(t("common:all", "All"))}
            </SelectItem>
            <SelectItem value="rtc">
              {String(t("writers:right-to-collect-col", "Right to collect"))}
            </SelectItem>
            <SelectItem value="no-rtc">
              {String(t("writers:no-right-to-collect", "No right to collect"))}
            </SelectItem>
          </SelectContent>
        </Select>
      </>
    ),
    [caeFilter, nameFilter, proFilter, rtcFilter, t],
  );

  return (
    <>
      <Dialog
        open={editingWriterCode !== null}
        onOpenChange={(open) => {
          if (!open)
            setEditingWriterCode(null);
        }}
      >
        <DialogContent className="max-w-2xl">
          <CreateWriterModal
            onWriterCreated={() => {}}
            onWriterUpdated={handleWriterUpdated}
            onClose={() => setEditingWriterCode(null)}
            editingWriterCode={editingWriterCode}
            availableWriters={data}
          />
        </DialogContent>
      </Dialog>

      <Table
        data={filteredData}
        columns={columns}
        highlightColour={highlightColour}
        getRowCanExpand={() => true}
        hasExpandableRows
        renderSubComponent={({ row }: { row: Row<Writer> }) => (
          <WriterDetails row={row} />
        )}
        filterContent={filters}
        isLoading={isLoading}
      />
    </>
  );
}

export default WritersTable;
